/**
 * RunAllModal — Multi-file translation batch selector with table layout.
 * 2 cột tick độc lập: Dịch + Kiểm tra. Header tick = tick all column.
 */

import React from 'react';
import { X, Zap, ShieldCheck, FileText, ShoppingCart, AlertTriangle, Check } from 'lucide-react';
import { TranslationFile } from '../../types';
import { formatCredits } from '../../services/mock/creditMock';
import { selectPrimaryCredits } from '../../utils/creditSelector';

interface RunAllModalProps {
  isOpen: boolean;
  onClose: () => void;
  pendingFiles: TranslationFile[];
  creditsAvailable: number;
  /** selectedIds = files cần dịch, qcIds = files cần QC (subset của selectedIds, ignored khi mode='train') */
  onConfirm: (selectedIds: string[], qcIds: string[]) => void;
  onBuyCredits: () => void;
  /** 'train' — dùng cho Huấn luyện: ẩn cột QC, dùng theme amber, tính credits chain train */
  mode?: 'translate' | 'train';
}

const getFileTranslateCredits = (f: TranslationFile, mode: 'translate' | 'train' = 'translate'): number => {
  const v = selectPrimaryCredits(f.estimatedCredits, mode);
  return typeof v === 'number' ? v : 0;
};

const getFileQCCredits = (f: TranslationFile): number => {
  const ec = f.estimatedCredits;
  return ec && typeof ec.qc === 'number' ? ec.qc : 0;
};

type TriState = 'all' | 'some' | 'none';

// Pure visual span — không tự click. Parent element handle click event để tránh
// nested-button HTML invalidity.
const TriCheckbox: React.FC<{
  state: TriState;
  variant?: 'emerald' | 'violet' | 'amber';
  size?: 'sm' | 'md';
}> = ({ state, variant = 'emerald', size = 'md' }) => {
  const base = size === 'sm' ? 'w-4 h-4' : 'w-[18px] h-[18px]';
  const colors = variant === 'emerald'
    ? { all: 'bg-emerald-600 border-emerald-600', some: 'bg-emerald-100 border-emerald-400', dash: 'bg-emerald-700' }
    : variant === 'amber'
      ? { all: 'bg-amber-600 border-amber-600', some: 'bg-amber-100 border-amber-400', dash: 'bg-amber-700' }
      : { all: 'bg-violet-600 border-violet-600', some: 'bg-violet-100 border-violet-400', dash: 'bg-violet-700' };
  return (
    <span
      className={`${base} rounded border-2 flex items-center justify-center shrink-0 transition-all ${
        state === 'all'
          ? colors.all
          : state === 'some'
            ? colors.some
            : 'bg-white border-slate-300'
      }`}
    >
      {state === 'all' && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3.5} />}
      {state === 'some' && <span className={`block w-2 h-[2px] ${colors.dash}`} />}
    </span>
  );
};

const RunAllModal: React.FC<RunAllModalProps> = ({
  isOpen,
  onClose,
  pendingFiles,
  creditsAvailable,
  onConfirm,
  onBuyCredits,
  mode = 'translate',
}) => {
  const isTrain = mode === 'train';
  const actionLabel = isTrain ? 'Huấn luyện' : 'Dịch';
  const titleText = isTrain ? 'Huấn luyện nhiều file' : 'Dịch nhiều file';
  const subtitleText = isTrain
    ? 'Tick từng file cần huấn luyện. Tick header = tick tất cả.'
    : 'Tick từng file cho cột Dịch + Kiểm tra. Tick header = tick tất cả cột.';
  const themeBgFrom = isTrain ? 'from-amber-50' : 'from-emerald-50';
  const themeViaTo = isTrain ? 'to-orange-50/40' : 'to-teal-50/40';
  const themeBlur = isTrain ? 'bg-amber-200/30' : 'bg-emerald-200/30';
  const themeIconBg = isTrain ? 'from-amber-500 to-orange-600' : 'from-emerald-500 to-teal-600';
  const themeIconShadow = isTrain ? 'shadow-amber-500/30' : 'shadow-emerald-500/30';
  const themeBadgeBg = isTrain ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700';
  const themeBtnBg = isTrain ? 'bg-amber-600 hover:bg-amber-700 shadow-amber-500/20' : 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-500/20';
  const themeBtnDisabled = isTrain ? 'disabled:hover:bg-amber-600' : 'disabled:hover:bg-emerald-600';
  const themeRowHover = isTrain ? 'hover:bg-amber-50/30' : 'hover:bg-emerald-50/30';
  const themeIconCol = isTrain ? 'text-amber-500' : 'text-emerald-500';
  const themeCheckBg = isTrain ? 'bg-amber-600 border-amber-600' : 'bg-emerald-600 border-emerald-600';
  const themeColAccent = isTrain ? 'amber' : 'emerald';
  const [translateIds, setTranslateIds] = React.useState<Set<string>>(new Set());
  const [qcIds, setQCIds] = React.useState<Set<string>>(new Set());

  React.useEffect(() => {
    if (isOpen) {
      const all = new Set(pendingFiles.map(f => f.id));
      setTranslateIds(all);
      // QC default theo skipQC heuristic
      const qcOn = pendingFiles.filter(f => !f.skipQC).map(f => f.id);
      setQCIds(new Set(qcOn));
    }
  }, [isOpen, pendingFiles]);

  if (!isOpen) return null;

  // State per column
  const total = pendingFiles.length;
  const translateCount = translateIds.size;
  const translateState: TriState = translateCount === 0 ? 'none' : translateCount === total ? 'all' : 'some';

  // QC state relative to TRANSLATE-selected files (chỉ file dịch mới được QC)
  const translateSelectedList = pendingFiles.filter(f => translateIds.has(f.id));
  const qcCount = translateSelectedList.filter(f => qcIds.has(f.id)).length;
  const qcState: TriState = translateSelectedList.length === 0
    ? 'none'
    : qcCount === 0
      ? 'none'
      : qcCount === translateSelectedList.length
        ? 'all'
        : 'some';

  // Credits
  const totalTranslate = translateSelectedList.reduce((s, f) => s + getFileTranslateCredits(f, isTrain ? 'train' : 'translate'), 0);
  const totalQC = isTrain
    ? 0
    : translateSelectedList
      .filter(f => qcIds.has(f.id))
      .reduce((s, f) => s + getFileQCCredits(f), 0);
  const totalCredits = totalTranslate + totalQC;
  const creditsAfter = creditsAvailable - totalCredits;
  const enough = creditsAvailable >= totalCredits;
  const noneSelected = translateCount === 0;

  // Handlers
  const toggleAllTranslate = () => {
    if (translateState === 'all' || translateState === 'some') {
      setTranslateIds(new Set());
      setQCIds(new Set());
    } else {
      setTranslateIds(new Set(pendingFiles.map(f => f.id)));
    }
  };

  const toggleAllQC = () => {
    if (qcState === 'all' || qcState === 'some') {
      setQCIds(new Set());
    } else {
      setQCIds(new Set(translateSelectedList.map(f => f.id)));
    }
  };

  const toggleFileTranslate = (id: string) => {
    setTranslateIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        setQCIds(qcPrev => {
          const qcNext = new Set(qcPrev);
          qcNext.delete(id);
          return qcNext;
        });
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleFileQC = (id: string) => {
    if (!translateIds.has(id)) return;
    setQCIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="fixed inset-0 z-[200] bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden border border-white/60 flex flex-col max-h-[88vh]">
        {/* Header */}
        <div className="relative shrink-0 overflow-hidden">
          <div className={`absolute inset-0 bg-gradient-to-br ${themeBgFrom} via-white ${themeViaTo}`} />
          <div className={`absolute -top-10 -right-10 w-40 h-40 ${themeBlur} rounded-full blur-3xl pointer-events-none`} />

          <div className="relative px-7 py-6 flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${themeIconBg} shadow-lg ${themeIconShadow} flex items-center justify-center shrink-0`}>
                <Zap className="w-5 h-5 text-white fill-current" />
              </div>
              <div>
                <h3 className="text-lg font-black text-slate-900 tracking-tight">
                  {titleText}
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  {subtitleText}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-white/70 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* ─── BODY: 2 framed cards ─────────────────────────── */}
        <div className="flex-1 overflow-y-auto bg-slate-50/60 px-5 py-5 space-y-4">
          {/* Card 1: SUMMARY frame */}
          <section className={`rounded-xl border bg-white overflow-hidden ${
            enough ? 'border-slate-200' : 'border-red-200'
          }`}>
            <div className="px-5 py-4">
              <div className="flex items-end justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-black tracking-widest uppercase text-slate-400 mb-2">
                    Tổng cộng
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 ${themeBadgeBg} rounded font-bold text-sm tabular-nums`}>
                      <Zap className="w-3 h-3 fill-current" />
                      {translateCount} {actionLabel.toLowerCase()}
                    </span>
                    {!isTrain && qcCount > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-violet-100 text-violet-700 rounded font-bold text-sm tabular-nums">
                        <ShieldCheck className="w-3 h-3" />
                        {qcCount} kiểm tra
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className={`text-3xl font-black tabular-nums leading-none ${enough ? 'text-slate-900' : 'text-red-600'}`}>
                    {formatCredits(totalCredits)}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-400 mt-1">
                    credits
                  </div>
                </div>
              </div>
            </div>
            {/* Inline status bar inside summary frame */}
            {enough ? (
              <div className="px-5 py-2.5 border-t border-slate-100 bg-slate-50/60 flex items-center justify-between text-xs">
                <span className="text-slate-500">Số dư sau khi chạy</span>
                <span className="text-slate-700 font-semibold tabular-nums">{formatCredits(creditsAfter)}</span>
              </div>
            ) : (
              <div className="px-5 py-2.5 border-t border-red-200/70 bg-red-50/60 flex items-center gap-1.5 text-xs text-red-700 font-semibold">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                Thiếu {formatCredits(totalCredits - creditsAvailable)} credits — nạp thêm để tiếp tục
              </div>
            )}
          </section>

          {/* Card 2: DETAIL TABLE frame */}
          <section className="rounded-xl border border-slate-200 bg-white overflow-hidden flex flex-col">
            <header className="px-5 py-3 border-b border-slate-100 bg-slate-50/60 flex items-center justify-between">
              <span className="text-[10px] font-black tracking-widest uppercase text-slate-500">
                Chi tiết file
              </span>
              <span className="text-[10px] font-semibold text-slate-400 tabular-nums">
                {pendingFiles.length} mục
              </span>
            </header>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white border-b border-slate-200">
                  <tr>
                    <th className="text-left px-5 py-2.5 text-[10px] font-black tracking-widest uppercase text-slate-500">
                      Tên file
                    </th>
                    <th className="text-center px-3 py-2.5 w-[130px]">
                      <button
                        onClick={toggleAllTranslate}
                        className={`inline-flex items-center gap-2 text-[10px] font-black tracking-widest uppercase text-slate-500 hover:text-${themeColAccent}-700 transition-colors`}
                      >
                        <TriCheckbox state={translateState} variant={isTrain ? 'amber' : 'emerald'} size="sm" />
                        <span className="inline-flex items-center gap-1">
                          <Zap className="w-3 h-3" /> {actionLabel}
                        </span>
                      </button>
                    </th>
                    {!isTrain && (
                      <th className="text-center px-3 py-2.5 w-[140px]">
                        <button
                          onClick={toggleAllQC}
                          disabled={translateSelectedList.length === 0}
                          className="inline-flex items-center gap-2 text-[10px] font-black tracking-widest uppercase text-slate-500 hover:text-violet-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <TriCheckbox state={qcState} variant="violet" size="sm" />
                          <span className="inline-flex items-center gap-1">
                            <ShieldCheck className="w-3 h-3" /> Kiểm tra
                          </span>
                        </button>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {pendingFiles.length === 0 ? (
                    <tr>
                      <td colSpan={isTrain ? 2 : 3} className="px-5 py-10 text-center text-sm text-slate-400">
                        {isTrain ? 'Không có file chờ huấn luyện.' : 'Không có file đang chờ dịch.'}
                      </td>
                    </tr>
                  ) : (
                    pendingFiles.map(file => {
                      const isTranslate = translateIds.has(file.id);
                      const isQC = qcIds.has(file.id);
                      const tr = getFileTranslateCredits(file, isTrain ? 'train' : 'translate');
                      const qc = getFileQCCredits(file);
                      return (
                        <tr
                          key={file.id}
                          className={`border-b border-slate-100 last:border-b-0 transition-colors ${
                            isTranslate ? themeRowHover : 'hover:bg-slate-50 opacity-60'
                          }`}
                        >
                          <td className="px-5 py-2.5 align-top">
                            <div className="flex items-start gap-2.5 min-w-0">
                              <FileText className={`w-4 h-4 shrink-0 mt-0.5 ${isTranslate ? themeIconCol : 'text-slate-300'}`} />
                              <span
                                className={`text-sm break-words leading-snug ${isTranslate ? 'text-slate-800 font-medium' : 'text-slate-400'}`}
                                title={file.fileName}
                              >
                                {file.fileName}
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-2.5 text-center align-top">
                            <button
                              onClick={() => toggleFileTranslate(file.id)}
                              className="inline-flex items-center gap-2 hover:opacity-70 transition-opacity"
                            >
                              <span className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-all ${
                                isTranslate ? themeCheckBg : 'bg-white border-slate-300'
                              }`}>
                                {isTranslate && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3.5} />}
                              </span>
                              <span className={`text-xs tabular-nums font-semibold ${
                                isTranslate ? 'text-slate-700' : 'text-slate-300'
                              }`}>
                                {isTranslate ? (tr > 0 ? formatCredits(tr) : '—') : '—'}
                              </span>
                            </button>
                          </td>
                          {!isTrain && (
                            <td className="px-3 py-2.5 text-center align-top">
                              <button
                                onClick={() => toggleFileQC(file.id)}
                                disabled={!isTranslate}
                                className="inline-flex items-center gap-2 hover:opacity-70 transition-opacity disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                <span className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-all ${
                                  isQC && isTranslate ? 'bg-violet-600 border-violet-600' : 'bg-white border-slate-300'
                                }`}>
                                  {isQC && isTranslate && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3.5} />}
                                </span>
                                <span className={`text-xs tabular-nums font-semibold ${
                                  isQC && isTranslate ? 'text-violet-600' : 'text-slate-300'
                                }`}>
                                  {isQC && isTranslate ? (qc > 0 ? `+${formatCredits(qc)}` : '—') : '—'}
                                </span>
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        {/* Actions */}
        <div className="shrink-0 px-7 py-4 bg-white border-t border-slate-100 flex gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
          >
            Hủy
          </button>
          {enough ? (
            <button
              onClick={() => onConfirm(Array.from(translateIds), Array.from(qcIds))}
              disabled={noneSelected}
              className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white ${themeBtnBg} shadow-lg transition-all active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed ${themeBtnDisabled}`}
            >
              <Zap className="w-4 h-4 fill-current" />
              {actionLabel} {translateCount} file
            </button>
          ) : (
            <button
              onClick={onBuyCredits}
              className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white ${themeBtnBg} shadow-lg transition-all active:scale-[0.98]`}
            >
              <ShoppingCart className="w-4 h-4" />
              Mua thêm credits
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RunAllModal;
